import { Link } from 'react-router-dom';
import { Cog, Check, Clock, Shield, ArrowRight, Phone, Zap, HelpCircle, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';
import { PORTAL_LINKS } from '../constants/site';

const PlantMachineryFinance = () => {
  const equipmentTypes = [
    'Excavators & Loaders',
    'Cranes & Hoists',
    'Bulldozers & Graders',
    'Concrete Equipment',
    'Drilling Rigs',
    'Generators',
    'Compressors',
    'Earthmoving Equipment'
  ];

  const industries = [
    'Construction',
    'Civil Engineering',
    'Mining',
    'Quarrying',
    'Demolition',
    'Earthmoving',
    'Road Works',
    'Infrastructure'
  ];

  const faqs = [
    {
      q: "What plant and machinery can I finance?",
      a: "We finance all types of plant and machinery including excavators, loaders, bulldozers, graders, cranes, drilling rigs, concrete equipment, generators, compressors, and more. Both new and used equipment is eligible."
    },
    {
      q: "How much can I borrow for plant and machinery?",
      a: "We arrange plant and machinery finance from $20,000 to over $5 million. For larger equipment purchases, we can structure deals across multiple lenders if needed."
    },
    {
      q: "Can I finance used plant and machinery?",
      a: "Yes, we finance both new and used plant and machinery. For used equipment, we consider age, condition, hours, and remaining useful life. Most lenders will finance equipment up to 15 years old."
    },
    {
      q: "What are the tax benefits of plant finance?",
      a: "Depending on the finance structure, you may be able to claim GST on the purchase, depreciation, and interest as business deductions. Chattel mortgage often provides the best tax outcomes. Consult your accountant for specific advice."
    },
    {
      q: "How fast can I get approved?",
      a: "Many plant and machinery finance applications are approved within 24-48 hours, depending on the applicant, the lender's credit criteria and the type of loan. For amounts under $250,000 with strong financials, faster approvals are often possible."
    },
    {
      q: "Do you finance plant at auction?",
      a: "Yes, we can arrange pre-approval for auction purchases so you're ready to bid with confidence. Contact us before the auction to get your finance in place."
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Plant & Machinery Finance Australia | Heavy Equipment Loans"
        description="Plant and machinery finance for Australian businesses. Finance excavators, loaders, cranes & earthmoving equipment. Loans from $20K to $5M+. Fast 24hr approvals. Call 1800 003 527."
        keywords="plant finance Australia, machinery finance, excavator finance, heavy equipment loans, construction equipment finance, earthmoving finance, crane finance, plant and machinery loans"
        canonical="/plant-machinery-finance"
      />

      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&w=1920&q=80)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/95 to-navy-900/80"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-sm mb-6">
                <Cog size={18} className="text-gold-500" />
                <span className="text-gold-500 font-body text-sm font-medium">Heavy Equipment Finance Specialists</span>
              </div>
              
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight leading-none mb-6">
                Plant & Machinery<br />
                <span className="gold-gradient-text">Finance</span>
              </h1>
              
              <p className="font-body text-lg text-white/70 mb-8 max-w-xl">
                Finance the heavy equipment your projects demand. From excavators to cranes, we help Australian construction and mining businesses acquire the plant and machinery they need with competitive rates and fast approvals.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a href={PORTAL_LINKS.COMMERCIAL} data-testid="plant-machinery-finance-hero-primary-link">
                  <Button 
                    data-testid="plant-finance-apply-btn"
                    className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-heading font-bold text-lg uppercase tracking-wider px-8 py-6 hover:scale-105 transition-transform shadow-lg shadow-purple-500/30"
                  >
                    <Sparkles size={20} className="mr-2" />
                    Get Free Quote
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </a>
                <a href="tel:+611800003527">
                  <Button 
                    variant="outline"
                    className="border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black font-heading font-bold text-lg uppercase tracking-wider px-8 py-6"
                  >
                    <Phone size={20} className="mr-2" />
                    1800 003 527
                  </Button>
                </a>
              </div>

              <div className="flex items-center gap-6 text-white/60">
                <div className="flex items-center gap-2">
                  <Clock size={18} className="text-gold-500" />
                  <span className="font-body text-sm">24-48hr Approvals</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={18} className="text-gold-500" />
                  <span className="font-body text-sm">Australian Owned</span>
                </div>
              </div>
            </div>

            <div className="bg-navy-800/80 backdrop-blur border border-white/10 p-8">
              <h3 className="font-heading font-bold text-2xl text-white uppercase mb-6">Quick Quote</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-white/10">
                  <span className="font-body text-white/60">Loan Amounts</span>
                  <span className="font-mono text-xl text-gold-500">$20K - $5M+</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/10">
                  <span className="font-body text-white/60">Loan Terms</span>
                  <span className="font-mono text-xl text-white">12 - 84 months</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/10">
                  <span className="font-body text-white/60">Approval Time</span>
                  <span className="font-mono text-xl text-gold-500 text-right">From 24-48 hours<span className="block font-body text-[10px] text-white/50">Depends on applicant, credit criteria &amp; loan type</span></span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-white/60">Deposit</span>
                  <span className="font-mono text-xl text-white">From $0</span>
                </div>
              </div>
              <Link to="/calculator" className="block mt-6">
                <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                  Calculate Repayments
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is Plant Finance */}
      <section className="py-20 bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-tight mb-6">
                What is Plant & Machinery Finance?
              </h2>
              <div className="space-y-4 font-body text-white/70">
                <p>
                  Plant and machinery finance is a specialised form of asset finance designed for heavy equipment used in construction, mining, earthmoving, and industrial applications. It allows businesses to acquire expensive equipment while preserving working capital.
                </p>
                <p>
                  As specialist <strong className="text-white">heavy equipment finance brokers</strong>, we understand the unique needs of the construction and mining sectors. We work with lenders who specialise in plant finance and understand equipment values.
                </p>
                <p>
                  Whether you're buying a single excavator or fitting out an entire project, we can structure finance to match your cash flow and project timelines.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {equipmentTypes.map((type, index) => (
                <div key={index} className="flex items-start gap-3 bg-navy-700 p-4">
                  <Check size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                  <span className="font-body text-white/80 text-sm">{type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-tight mb-4">
              Industries We Serve
            </h2>
            <p className="font-body text-white/60 max-w-2xl mx-auto">
              Plant and machinery finance for heavy industries across Australia
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {industries.map((industry, index) => (
              <div key={index} className="bg-navy-800 border border-white/5 p-6 text-center hover:border-gold-500/30 transition-colors">
                <span className="font-heading font-bold text-white uppercase">{industry}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-sm mb-4">
              <HelpCircle size={16} className="text-gold-500" />
              <span className="text-gold-500 font-body text-sm font-medium">Common Questions</span>
            </div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-tight">
              Plant & Machinery Finance FAQ
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`}
                className="bg-navy-700 border border-white/5 px-6"
              >
                <AccordionTrigger className="text-left font-heading font-bold text-lg text-white hover:text-gold-500 py-6">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="font-body text-white/70 pb-6 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gold-gradient">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-black uppercase tracking-tight mb-6">
            Ready for Plant & Machinery Finance?
          </h2>
          <p className="font-body text-black/70 text-lg mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation quote. Our team specialises in heavy equipment finance for Australian businesses.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={PORTAL_LINKS.COMMERCIAL} data-testid="plant-machinery-finance-final-primary-link">
              <Button 
                className="bg-navy-900 text-white hover:bg-navy-800 font-heading font-bold text-lg uppercase tracking-wider px-8 py-6"
                data-testid="plant-machinery-finance-final-primary-cta"
              >
                <Sparkles size={20} className="mr-2" />
                Get Free Quote
              </Button>
            </a>
            <a href="tel:+611800003527">
              <Button 
                variant="outline"
                className="border-black text-black hover:bg-black hover:text-gold-500 font-heading font-bold text-lg uppercase tracking-wider px-8 py-6"
              >
                Call 1800 003 527
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlantMachineryFinance;
